import React from 'react';

import { LmText } from '@mes-ui/lemma';
import { Line } from '@reactchartjs/react-chart.js';
import { AVERAGE_SUBJECTS_VALUE } from 'components/redesignStudent/PerformanceChart/consts';

export interface IChartHorizontalLine {
  name: string;
  values: {
    interval?: string;
    value?: number;
  }[];
  color?: string;
}
interface ILineChartProps {
  incomingData: IChartHorizontalLine[];
  width?: number;
  yAxisLabel?: string;
}

// Плагин для самостоятельной отрисовки линий и подписей под осью X
const customGridPlugin = {
  beforeDraw(chart: any) {
    const ctx = chart.chart.ctx;
    const xAxis = chart.scales['x-axis-0'];
    const yAxis = chart.scales['y-axis-0'];

    ctx.save();

    ctx.strokeStyle = '#D5D6DC';
    ctx.lineWidth = 1;

    // Вертикальные линии
    for (let i = 0; i < xAxis.ticks.length; i++) {
      const x = xAxis.getPixelForTick(i);

      ctx.beginPath();

      ctx.setLineDash([4, 4]);
      ctx.moveTo(x, yAxis.bottom + 10);
      ctx.lineTo(x, yAxis.bottom);
      ctx.stroke();

      ctx.setLineDash([8, 8]);
      ctx.beginPath();
      ctx.moveTo(x, yAxis.bottom);
      ctx.lineTo(x, yAxis.top);
      ctx.stroke();

      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.moveTo(x, yAxis.top);
      ctx.lineTo(x, yAxis.top - 11);
      ctx.stroke();
    }

    // Горизонтальные линии
    ctx.setLineDash([]);
    ctx.strokeStyle = '#D5D6DC';
    ctx.lineWidth = 1;

    for (let i = 0; i < yAxis.ticks.length; i++) {
      const y = yAxis.getPixelForTick(i);

      ctx.beginPath();
      ctx.moveTo(xAxis.left - 29, y);
      ctx.lineTo(xAxis.right + 43, y);
      ctx.stroke();
    }

    // Подписи оси X
    ctx.fillStyle = '#0A0B0FA6';
    ctx.font = '600 12px Inter';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    for (let i = 0; i < xAxis.ticks.length; i++) {
      const x = xAxis.getPixelForTick(i);
      const label = xAxis.ticks[i];

      ctx.fillText(
        label,
        x + 8,
        yAxis.bottom + 18, // отступ от нижней линии графика
      );
    }

    ctx.restore();
  },
};

const CHART_WIDTH_PER_YEAR = 80;

const LineChart: React.FC<ILineChartProps> = ({ incomingData, yAxisLabel }) => {
  const yearsCount = incomingData[0]?.values.length ?? 0;
  const width = yearsCount > 6 ? yearsCount * CHART_WIDTH_PER_YEAR : undefined;

  const data = () => ({
    labels: incomingData[0]?.values.map((value) => value.interval),
    datasets: incomingData?.map((performance) => ({
      label: performance.name,
      data: performance.values.map((item) => item.value),
      fill: false,
      borderColor: performance.color,
      borderWidth: performance.name === AVERAGE_SUBJECTS_VALUE ? 4 : 2,
      lineTension: 0,
      pointRadius: performance.name === AVERAGE_SUBJECTS_VALUE ? 8 : 6,
      pointBorderWidth: performance.name === AVERAGE_SUBJECTS_VALUE ? 4 : 2,
      pointBackgroundColor: 'white',
      pointBorderColor: performance.color,
    })),
  });

  const options = {
    maintainAspectRatio: false,
    legend: {
      display: false,
    },
    spanGaps: true,
    tooltips: {
      backgroundColor: 'rgba(43, 56, 88, 0.8)',
      titleAlign: 'center',
      bodyAlign: 'center',
      titleFontFamily: 'Lato',
      bodyFontFamily: 'Lato',
      displayColors: false,
      titleSpacing: 4,
      bodySpacing: 4,
      callbacks: {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        title() {},
        label(tooltipItem: any, data: any) {
          const label = data.datasets[tooltipItem.datasetIndex].label || '';
          return label;
        },
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        beforeLabel() {},
        afterLabel(tooltipItem: any) {
          return `Ср. балл = ${tooltipItem.yLabel}`;
        },
      },
    },
    plugins: {
      datalabels: {
        display: false,
      },
    },
    scales: {
      yAxes: [
        {
          gridLines: {
            display: false,
          },
          ticks: {
            min: 2,
            max: 5,
            stepSize: 1,
            fontColor: '#0A0B0FA6',
            fontStyle: '500',
            fontFamily: 'Inter',
            fontSize: 14,
            padding: 32,
          },
        },
      ],
      xAxes: [
        {
          gridLines: {
            display: false,
          },
          ticks: {
            display: false,
          },
        },
      ],
    },
    layout: {
      padding: {
        top: 11,
        bottom: 48,
        right: 45,
      },
    },
  };

  return (
    <div className="line" style={{ width, minWidth: '100%' }}>
      {yAxisLabel && (
        <LmText variant="lg" color="note" className="line-chart__axis-y">
          {yAxisLabel}
        </LmText>
      )}
      {/** key={yearsCount} используется для полной перерисовки графика при измении его размера по оси X */}
      <Line key={yearsCount} data={data} options={options} plugins={[customGridPlugin]} />
    </div>
  );
};

export default LineChart;
